import { TimeBar } from "@/phaser/ui/game/TimeBar";
import { LeftBtn } from "@phaser/ui/btns/LeftBtn";
import { RightBtn } from "@phaser/ui/btns/RightBtn";
import { ScoreBoard } from "@/phaser/ui/game/ScoreBoard";
import { ScoreManager } from "@/phaser/core/game/ScoreManager";
import { ItemQueue } from "@phaser/core/queueItem/ItemQueue";

import { type StageConfig } from "@/phaser/core/monsters/monsterCatalog";
import {
  calcEarnedCoins,
  GameRuleManager,
} from "@/phaser/core/game/GameRuleManager";

import { type PluginListenerHandle } from "@capacitor/core";
import { App } from "@capacitor/app";
import { ModalManager, ModalType } from "@phaser/ui/modals/ModalManager";
import {
  GameStatus,
  GameStateController,
} from "@/phaser/core/game/GameStateController";
import { Game } from "@phaser/Game";
import { calcTimeSpeedByScore } from "@/utils/calcTime";
import { STAGES, type StageId } from "@phaser/core/stages/StageCatalog";
import type {
  QueueItem,
  ResolveInput,
  ResolveResult,
} from "@phaser/core/queueItem/queueItem";
import { getObstacleById } from "@/phaser/core/obstacles/obstaclesCatalog";
import { DefaultQueueItemProvider } from "@phaser/core/queueItem/QueueItemProvider";
import { ObstacleFailureHandler } from "@phaser/core/obstacles/ObstacleFailureHandler";
import { updateHighScore } from "@/phaser/core/storage/highScoreStorage";
import { monsterCountManager } from "@/phaser/core/monsters/MonsterCountManager";
import { GameDataCache } from "@phaser/core/cache/GameDataCache";
import { coinManager } from "@/phaser/core/game/CoinManager";
import { shopEquipManager } from "@phaser/core/shop/ShopEquipManager";
import { ARROW_SKIN_BY_ID } from "@phaser/core/skins/arrowSkinCatalog";
import {
  createComboText,
  createMonsterPortalEffect,
  createstageCrackEffect,
  playGameOverTextEffect,
  showComboScoreEffect,
  updateComboText,
} from "@phaser/core/effect/GameEffect";
import { registerGameInput } from "@phaser/core/game/RegisterGameInput";
import {
  registerAppLifecycleEvents,
  registerGameStateEvents,
} from "@phaser/core/game/RegisterGameLifeEvent";
import { CountdownTimer } from "@phaser/core/game/CountdownTimer";
import { DEFAULT_SKIN_ID } from "../core/skins/ArrowSkinManager";

export class GameScene extends Phaser.Scene {
  // =============================
  // Scene State
  // =============================
  private state!: GameStateController;
  private pausedByBackground = false;
  private backSub?: PluginListenerHandle;

  // =============================
  // Stage Data
  // =============================
  private stageId!: StageId;
  private stage!: StageConfig;
  private stageBgKey!: string;
  private stageMonsters!: ReturnType<
    typeof GameDataCache.getStageData
  >["monsters"];

  // =============================
  // Core Game Systems
  // =============================
  private timer!: CountdownTimer;
  private ruleManager!: GameRuleManager;
  private scoreManager!: ScoreManager;
  private queueItemProvider!: DefaultQueueItemProvider;
  private obstacleFailureHandler!: ObstacleFailureHandler;

  // =============================
  // Gameplay Objects
  // =============================
  private itemQueue!: ItemQueue;

  // =============================
  // UI
  // =============================
  private timeBar!: TimeBar;
  private scoreBoard!: ScoreBoard;
  private leftBtn!: LeftBtn;
  private rightBtn!: RightBtn;
  private modal!: ModalManager;
  private comboText!: Phaser.GameObjects.Text;

  constructor() {
    super("GameScene");
  }

  init(data: { stageId?: StageId }) {
    this.state = new GameStateController();

    this.stageId = data.stageId ?? STAGES[0].id;

    const prepared = GameDataCache.getStageData(this.stageId);
    this.stage = prepared.stage;
    this.stageBgKey = prepared.meta.bgKey;
    this.stageMonsters = prepared.monsters;
  }

  create() {
    const { width, height } = this.scale;
    this.input.addPointer(2);

    // =============================
    // 1. Stage / Skin Data
    // =============================
    const equippedArrowId =
      shopEquipManager.getEquippedItemId("ARROW_SKIN") ?? "arrow-001";

    const skin =
      ARROW_SKIN_BY_ID[equippedArrowId] ?? ARROW_SKIN_BY_ID[DEFAULT_SKIN_ID]!;

    const stageObstacles = this.stage.obstacleIds.map(getObstacleById);

    // =============================
    // 2. Core Game Systems
    // =============================
    this.queueItemProvider = new DefaultQueueItemProvider({
      monsters: this.stageMonsters,
      obstacles: stageObstacles,
    });

    this.timer = new CountdownTimer({
      totalTimeSec: 5,
      onTimeout: () => this.handleTimeOver(),
    });

    this.scoreManager = new ScoreManager();

    this.ruleManager = new GameRuleManager({
      stage: this.stage,
      scoreManager: this.scoreManager,
      timer: this.timer,
    });

    this.obstacleFailureHandler = new ObstacleFailureHandler({
      scene: this,
      scoreManager: this.scoreManager,
    });

    this.modal = new ModalManager(this, this.state);

    // =============================
    // 3. Events
    // =============================
    const game = this.game as Game;

    registerAppLifecycleEvents({
      scene: this,
      game,
      getStatus: () => this.state.status,
      pause: () => this.state.pause(),
      getPausedByBackground: () => this.pausedByBackground,
      setPausedByBackground: (value) => {
        this.pausedByBackground = value;
      },
    });

    registerGameStateEvents({
      scene: this,
      subscribeChanged: (callback) => this.state.on("changed", callback),
      subscribeExitHome: (callback) => this.state.onExitHome(callback),
      onPause: () => this.applyPause(),
      onResume: () => this.applyResume(),
      onGameOver: () => void this.applyGameOver(),
      onExitHome: () => {
        this.anims.resumeAll();
        this.tweens.resumeAll();
        this.physics?.world?.resume?.();

        this.scene.start("HomeScene");
      },
    });

    // =============================
    // 4. Background
    // =============================
    const bg = this.add.image(width / 2, height / 2, this.stageBgKey);
    bg.setDisplaySize(width, height);

    // =============================
    // 5. UI
    // =============================
    createstageCrackEffect(this);

    this.timeBar = new TimeBar(this, width / 2, height * 0.14);
    this.scoreBoard = new ScoreBoard(this, width / 2, height * 0.2);

    createMonsterPortalEffect(this);

    this.comboText = createComboText(this);

    this.leftBtn = new LeftBtn(
      this,
      width - width * 0.77,
      height - height * 0.12,
      {
        textureKey: skin.textureKey,
        onClick: () => {
          if (this.state.isGameOver) return;
          this.handleDirectionPress("LEFT");
        },
      },
    );

    this.rightBtn = new RightBtn(
      this,
      width - width * 0.23,
      height - height * 0.12,
      {
        textureKey: skin.textureKey,
        onClick: () => {
          if (this.state.isGameOver) return;
          this.handleDirectionPress("RIGHT");
        },
      },
    );

    // =============================
    // 6. Gameplay Objects
    // =============================
    this.itemQueue = new ItemQueue(this, {
      width,
      height,
      itemProvider: this.queueItemProvider,
      onItemResolved: (item, input) =>
        this.handleQueueItemResolved(item, input),
    });

    // =============================
    // 7. Input
    // =============================
    registerGameInput({
      scene: this,
      state: this.state,
      leftBtn: this.leftBtn,
      rightBtn: this.rightBtn,
      onPause: () => this.state.togglePause(),
      registerBackButton: () => this.registerBackButton(),
    });

    // =============================
    // 8. Start
    // =============================
    this.state.start();
    this.timer.start();

    // =============================
    // 9. Cleanup
    // =============================
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.backSub?.remove();
      this.backSub = undefined;
      this.state.destroy();
    });
  }

  update() {
    if (this.state.status !== GameStatus.RUNNING) return;
    this.timer.tick();
    this.timeBar.setProgress(this.timer.getProgress());
  }

  /**
   * 몬스터가 큐에서 빠질 때 호출되는 로직
   * - 여기에서 "이 몬스터가 왼쪽이 정답인지 / 오른쪽이 정답인지" 판정
   * - 점수 증가 / 감소 등 게임 규칙을 구현
   */
  private handleQueueItemResolved(
    item: QueueItem,
    input: ResolveInput,
  ): ResolveResult {
    if (this.state.isGameOver) {
      return { success: true, consume: true };
    }

    if (item.kind === "OBSTACLE" || item.kind === "MONSTER") {
      const result = this.ruleManager.handleResolved(
        item.data.habitat,
        input.type,
      );

      updateComboText(this, this.comboText, result.combo);

      if (item.kind === "OBSTACLE" && !result.isCorrect) {
        this.obstacleFailureHandler.handle(item.data);
      }

      if (item.kind === "MONSTER" && result.isCorrect) {
        void monsterCountManager.increment(item.data.id);
      }

      this.applyPostResolveEffects(result.comboBonus);

      return {
        success: result.isCorrect,
        consume: true,
      };
    }

    return {
      success: true,
      consume: true,
    };
  }

  private applyPostResolveEffects(comboBonus: number) {
    const score = this.scoreManager.getScore();

    this.queueItemProvider.updateByGameState(score);
    this.scoreBoard.setScore(score);

    const speed = calcTimeSpeedByScore(score);
    this.timer.setSpeedMultiplier(speed);

    if (comboBonus > 0) {
      showComboScoreEffect(this, comboBonus);
    }
  }

  // 방향 입력 처리
  private handleDirectionPress(direction: "LEFT" | "RIGHT") {
    const currentItem = this.itemQueue.getCurrentQueueItem();

    if (!currentItem) return;
    if (this.state.status !== GameStatus.RUNNING) return;
    if (this.state.isGameOver) return;

    this.itemQueue.proceed({ type: direction });
  }

  /** 게임이 종료 되었을 때 처리 */
  private handleTimeOver() {
    if (this.state.isGameOver) return;
    this.state.gameOver();
  }

  // 게임 일시정지 처리
  private applyPause() {
    this.timer.pause();
    this.leftBtn.disableInteractive();
    this.rightBtn.disableInteractive();

    this.tweens.pauseAll();
    this.anims.pauseAll();
    this.physics?.world?.pause?.();
    this.obstacleFailureHandler.pauseEffects();

    this.modal.open(ModalType.PAUSE);
  }

  // 게임 재개 처리
  private applyResume() {
    this.modal.close();

    this.timer.start();

    this.leftBtn.setInteractive();
    this.rightBtn.setInteractive();

    this.tweens.resumeAll();
    this.anims.resumeAll();
    this.physics?.world?.resume?.();
    this.obstacleFailureHandler.resumeEffects();
  }

  // 게임 오버 처리
  private async applyGameOver() {
    this.input.enabled = false;
    this.leftBtn.disableInteractive();
    this.rightBtn.disableInteractive();
    this.leftBtn.setVisible(false);
    this.rightBtn.setVisible(false);

    const finalScore = this.scoreManager.getScore();
    const earnedCoins = calcEarnedCoins(finalScore);

    const currentCoins =
      earnedCoins > 0
        ? await coinManager.addCoins(earnedCoins)
        : coinManager.getBalance();

    const { bestScore, isNewRecord } = await updateHighScore(finalScore);

    this.registry.set("finalScore", finalScore);
    this.registry.set("bestScore", bestScore);
    this.registry.set("isNewRecord", isNewRecord);
    this.registry.set("earnedCoins", earnedCoins);
    this.registry.set("currentCoins", currentCoins);

    playGameOverTextEffect(this, () => {
      this.scene.start("GameOverScene");
    });
  }

  // 뒤로가기 버튼 등록
  private async registerBackButton() {
    this.backSub = await App.addListener("backButton", () => {
      this.state.togglePause();
    });
  }
}
